/**
 * Which tutorials an operator has already been shown (issue #427).
 *
 * Progress is stored per operator — keyed by the OIDC `sub` — so a shared
 * browser never leaks one operator's progress to another, and signing in as
 * somebody new starts their own onboarding. Client-side only: this is a
 * convenience, and losing it costs one repeated tutorial, not any mission data.
 */
import { safeLocalStorage } from '../utils/storage';
import { TOURS, Tour } from './tours';

const KEY_PREFIX = 'drone-fleet-tutorial:';

/** Ids of the tours this operator has completed or skipped. */
export const seenTours = (userKey: string): string[] => {
  const storage = safeLocalStorage();
  if (!storage) {
    return [];
  }
  try {
    const raw = storage.getItem(`${KEY_PREFIX}${userKey}`);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
  } catch (e) {
    // A corrupt entry must not block the app — treat it as "nothing seen".
    console.warn('Failed to read tutorial progress:', e);
    return [];
  }
};

/** Record a tour as done (completed or skipped) so it never runs again. */
export const markTourSeen = (userKey: string, tourId: string): void => {
  const storage = safeLocalStorage();
  if (!storage) {
    return;
  }
  const seen = seenTours(userKey);
  if (seen.includes(tourId)) {
    return;
  }
  try {
    storage.setItem(`${KEY_PREFIX}${userKey}`, JSON.stringify([...seen, tourId]));
  } catch (e) {
    console.warn('Failed to save tutorial progress:', e);
  }
};

/** Tours this operator has not been shown yet, oldest first. */
export const pendingTours = (userKey: string): Tour[] => {
  const seen = seenTours(userKey);
  return TOURS.filter((tour) => !seen.includes(tour.id));
};
